import * as React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

import { PageContext } from "../../../../context/numbered-pagination";
import {
  PageLengthDescription,
  PageLengthSelect,
  PageSelector,
  PageControl,
  PageNumber,
  PagePrev,
  PageNext,
} from "../Pagination.styles";

interface IPaginationProps {
  className?: string;
  total: number;
}

const CustomPagination: React.FC<IPaginationProps> = ({ className, total }) => {
  const { pageIndex, perPage, setPageIndex, setPerPage } = React.useContext(PageContext);
  const indicesCount = 3;
  const totalPages = Math.ceil(total / perPage);
  const prevPage = () => setPageIndex(pageIndex - 1);
  const nextPage = () => setPageIndex(pageIndex + 1);
  const goToPage = (pageNumber: number) => () => setPageIndex(pageNumber);
  const selectPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => setPerPage(parseInt(e.target.value, 10));
  const genIndices = (size: number) => new Array(size).fill(1).map((_, idx) => idx);
  const genPageNumbers = () => {
    if (totalPages === 0) {
      return <span> - </span>;
    }

    if (totalPages <= indicesCount) {
      return (
        <>
          {genIndices(totalPages).map(idx => (
            <PageNumber key={idx} onClick={goToPage(idx)} selected={pageIndex === idx}>
              {pageIndex === idx ? <strong>{idx + 1}</strong> : idx + 1}
            </PageNumber>
          ))}
        </>
      );
    }

    if (pageIndex <= totalPages - (indicesCount + 1)) {
      return (
        <>
          {pageIndex > 0 && (
            <>
              <PageNumber onClick={goToPage(0)} selected={pageIndex === 0}>
                1
              </PageNumber>
              ...
            </>
          )}
          {genIndices(indicesCount).map(idx => (
            <PageNumber key={idx} onClick={goToPage(pageIndex + idx)} selected={idx === 0}>
              {idx === 0 ? <strong>{pageIndex + idx + 1}</strong> : pageIndex + idx + 1}
            </PageNumber>
          ))}
          ...
          <PageNumber onClick={goToPage(totalPages - 1)} selected={false}>
            {totalPages}
          </PageNumber>
        </>
      );
    }

    return (
      <>
        <PageNumber onClick={goToPage(0)} selected={false}>
          1
        </PageNumber>
        ...
        {genIndices(indicesCount)
          .reverse()
          .map((idx, arrayIdx) => (
            <PageNumber
              key={arrayIdx}
              onClick={goToPage(totalPages - (idx + 1))}
              selected={pageIndex === totalPages - (idx + 1)}
            >
              {pageIndex === totalPages - (idx + 1) ? <strong>{totalPages - idx}</strong> : totalPages - idx}
            </PageNumber>
          ))}
      </>
    );
  };

  return (
    <PageControl className={className}>
      <PageLengthDescription>
        Rows per page:
        <PageLengthSelect value={perPage} onChange={selectPerPage} data-testid="page-length-select">
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={500}>500</option>
        </PageLengthSelect>{" "}
      </PageLengthDescription>
      <PageSelector>
        <PagePrev disabled={pageIndex === 0} Icon={FaChevronLeft} onClick={prevPage} />
        {genPageNumbers()}
        <PageNext disabled={pageIndex >= totalPages - 1} Icon={FaChevronRight} onClick={nextPage} />
      </PageSelector>
    </PageControl>
  );
};

export default CustomPagination;
